import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Trophy, Swords, Loader2 } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface SearchResult {
  id: string;
  type: 'player' | 'tournament' | 'match';
  title: string;
  subtitle?: string;
  href: string;
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Keyboard shortcut: Ctrl/Cmd + K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Search function
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    const searchResults: SearchResult[] = [];

    try {
      // Search players using public view (excludes profile_id for privacy)
      const { data: players } = await supabase
        .from('players_public')
        .select('id, name, slug, position, game_id')
        .ilike('name', `%${searchQuery}%`)
        .limit(5);

      if (players) {
        players.forEach((player: any) => {
          searchResults.push({
            id: player.id,
            type: 'player',
            title: player.name,
            subtitle: player.game_id ? `ID: ${player.game_id} • ${player.position || 'Player'}` : (player.position || 'Player'),
            href: `/players/${player.slug}`,
          });
        });
      }

      // Search tournaments
      const { data: tournaments } = await supabase
        .from('tournaments')
        .select('id, name, status, type')
        .ilike('name', `%${searchQuery}%`)
        .neq('status', 'draft')
        .limit(5);

      if (tournaments) {
        tournaments.forEach((tournament) => {
          searchResults.push({
            id: tournament.id,
            type: 'tournament',
            title: tournament.name,
            subtitle: `${tournament.status} • ${tournament.type || 'League'}`,
            href: '/tournaments',
          });
        });
      }

      // Search matches by player names or tournament
      const { data: matches } = await supabase
        .from('matches')
        .select(`
          id, status, match_date,
          player_a:players!matches_team_a_fkey(name),
          player_b:players!matches_team_b_fkey(name),
          tournament:tournaments(name)
        `)
        .or(`team_a_name.ilike.%${searchQuery}%,team_b_name.ilike.%${searchQuery}%`)
        .limit(5);

      if (matches) {
        matches.forEach((match: any) => {
          const playerA = match.player_a?.name || 'TBD';
          const playerB = match.player_b?.name || 'TBD';
          searchResults.push({
            id: match.id,
            type: 'match',
            title: `${playerA} vs ${playerB}`,
            subtitle: match.tournament?.name || match.status,
            href: '/matches',
          });
        });
      }

      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  const handleSelect = (result: SearchResult) => {
    setOpen(false);
    setQuery('');
    navigate(result.href);
  };

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'player':
        return <User className="h-4 w-4 text-primary" />;
      case 'tournament':
        return <Trophy className="h-4 w-4 text-warning" />;
      case 'match':
        return <Swords className="h-4 w-4 text-secondary" />;
    }
  };

  const playerResults = results.filter((r) => r.type === 'player');
  const tournamentResults = results.filter((r) => r.type === 'tournament');
  const matchResults = results.filter((r) => r.type === 'match');

  return (
    <>
      <Button
        variant="outline"
        className="relative h-9 w-9 p-0 xl:h-10 xl:w-60 xl:justify-start xl:px-3 xl:py-2 border-border hover:border-primary"
        onClick={() => setOpen(true)}
        aria-label="Search"
      >
        <Search className="h-4 w-4 xl:mr-2" />
        <span className="hidden xl:inline-flex text-muted-foreground">Search...</span>
        <kbd className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 hidden h-6 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 xl:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search players, tournaments, matches..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {!isLoading && query && results.length === 0 && (
            <CommandEmpty>No results found for "{query}"</CommandEmpty>
          )}

          {!isLoading && !query && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type to search across players, tournaments, and matches
            </div>
          )}

          {!isLoading && playerResults.length > 0 && (
            <CommandGroup heading="Players">
              {playerResults.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.title}
                  onSelect={() => handleSelect(result)}
                  className="cursor-pointer"
                >
                  {getIcon(result.type)}
                  <div className="ml-3 flex-1">
                    <p className="font-medium">{result.title}</p>
                    {result.subtitle && (
                      <p className="text-sm text-muted-foreground">{result.subtitle}</p>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {!isLoading && playerResults.length > 0 && (tournamentResults.length > 0 || matchResults.length > 0) && (
            <CommandSeparator />
          )}

          {!isLoading && tournamentResults.length > 0 && (
            <CommandGroup heading="Tournaments">
              {tournamentResults.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.title}
                  onSelect={() => handleSelect(result)}
                  className="cursor-pointer"
                >
                  {getIcon(result.type)}
                  <div className="ml-3 flex-1">
                    <p className="font-medium">{result.title}</p>
                    {result.subtitle && (
                      <p className="text-sm text-muted-foreground">{result.subtitle}</p>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {!isLoading && tournamentResults.length > 0 && matchResults.length > 0 && (
            <CommandSeparator />
          )}

          {!isLoading && matchResults.length > 0 && (
            <CommandGroup heading="Matches">
              {matchResults.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.title}
                  onSelect={() => handleSelect(result)}
                  className="cursor-pointer"
                >
                  {getIcon(result.type)}
                  <div className="ml-3 flex-1">
                    <p className="font-medium">{result.title}</p>
                    {result.subtitle && (
                      <p className="text-sm text-muted-foreground">{result.subtitle}</p>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
